import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface UpdateProfileData {
  profession?: string;
  business_type?: string;
  jiu_jitsu_school?: string;
  company?: string;
  instagram?: string;
  x?: string;
  facebook?: string;
  youtube?: string;
  website?: string;
  linkedin?: string;
  main_academy_id?: string;
  [key: string]: any;
}

export const useUpdateProfile = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (profileData: UpdateProfileData) => {
      if (!user) {
        throw new Error('User must be authenticated to update profile');
      }

      // Get profession_id if profession name is provided
      let profession_id = null;
      if (profileData.profession) {
        const { data: professionData } = await supabase
          .from('professions')
          .select('id')
          .eq('name', profileData.profession)
          .single();
        profession_id = professionData?.id || null;
      }

      // Get business_type_id if business_type name is provided
      let business_type_id = null;
      if (profileData.business_type) {
        const { data: businessTypeData } = await supabase
          .from('business_types')
          .select('id')
          .eq('name', profileData.business_type)
          .single();
        business_type_id = businessTypeData?.id || null;
      }

      // Prepare the data for the update function
      const updateData = {
        ...profileData,
        profession_id,
        business_type_id,
      };

      const { data, error } = await supabase.rpc('update_profile', {
        profile_id: user.id,
        profile_data: updateData
      });
  
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Invalidate and refetch profile data
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}; 